// src/pages/QuizRunner.js

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import ProgressBar from "react-bootstrap/ProgressBar";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getMe,
  getQuizResults,
  createQuizResult,
  upsertLessonProgress,
  getModule,
  aiChat,
  aiFeedback,
} from "../api/client";
import {
  Clock,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Trophy,
  Cpu,
} from "lucide-react";
import { useTranslation } from "../hooks/useTranslation";

export default function QuizRunner() {
  const { t } = useTranslation(); 
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [moduleData, setModuleData] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  
  // For single_choice
  const [selectedIndex, setSelectedIndex] = useState(null);
  
  // For input and multi-part input
  const [inputValue, setInputValue] = useState("");
  const [multiInputValues, setMultiInputValues] = useState({});
  
  // For dropdown
  const [dropdownValues, setDropdownValues] = useState({});
  
  // For container sub-questions
  const [subQuestionsAnswers, setsubQuestionsAnswers] = useState({});
  
  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [user, setUser] = useState(null);
  const [myResults, setMyResults] = useState([]);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [savedResult, setSavedResult] = useState(false);
  const [aiFeedbackText, setAiFeedbackText] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        if (!me || !me.id) return;
        setUser(me);

        const res = await getQuizResults(me.id);
        const list = Array.isArray(res) ? res : [];
        setMyResults(list);

        if (
          lessonId &&
          list.some((r) => r.quiz_id === lessonId && r.user_id === me.id)
        ) {
          setAlreadyCompleted(true);
        }
      } catch (e) {
        console.error("QuizRunner user/results error:", e);
      }
    })();
  }, [lessonId]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getModule(moduleId || "M1_WR_1K");
        if (!data || !Array.isArray(data.lessons)) {
          throw new Error("invalid_module");
        }

        const les =
          data.lessons.find((l) => l.id === lessonId) || data.lessons[0];
        if (!les) {
          throw new Error("lesson_not_found");
        }

        setModuleData(data);
        setLesson(les);

        const questionsCount = Array.isArray(les.exercises)
          ? les.exercises.length
          : 0;
        const perQuestion = questionsCount * 60 || 5 * 60;
        const byDuration = (les.duration_minutes || 10) * 60;
        setTimeLeft(Math.max(60, Math.min(perQuestion, byDuration)));

        // Load saved answers from localStorage
        const savedAnswersKey = `quiz_answers_${lessonId}`;
        const savedAnswers = localStorage.getItem(savedAnswersKey);
        if (savedAnswers) {
          try {
            const parsedAnswers = JSON.parse(savedAnswers);
            setAnswers(parsedAnswers);
          } catch (e) {
            console.error("Error parsing saved answers:", e);
          }
        }
      } catch (e) {
        console.error("QuizRunner load error:", e);
        setError(t.quiz.error_loading);
      } finally {
        setLoading(false);
      }
    })();
  }, [moduleId, lessonId]);

  // Save answers to localStorage whenever they change
  useEffect(() => {
    if (lessonId && answers.length > 0) {
      const savedAnswersKey = `quiz_answers_${lessonId}`;
      localStorage.setItem(savedAnswersKey, JSON.stringify(answers));
    }
  }, [answers, lessonId]);

  // Clear localStorage when quiz is finished
  useEffect(() => {
    if (finished && lessonId) {
      const savedAnswersKey = `quiz_answers_${lessonId}`;
      localStorage.removeItem(savedAnswersKey);
    }
  }, [finished, lessonId]);

  const questions = useMemo(() => {
  if (!lesson || !Array.isArray(lesson.exercises)) return [];
  
  // Filtere nur Fragen die KEINE Teilfragen sind
  const mainQuestions = lesson.exercises.filter(ex => !ex.parentId);
  
  return mainQuestions.map((ex, idx) => {
    // Wenn es ein Container ist, finde alle Teilfragen
    if (ex.isContainer) {
      const subQuestions = lesson.exercises
        .filter(subEx => subEx.parentId === ex.id)
        .map(subEx => ({
          id: subEx.id,
          title: subEx.title || "",
          promptmarkdown: subEx.promptmarkdown || "",
          solutionmarkdown: subEx.solutionmarkdown || "",
          question: subEx.question || {},
          pointsreward: subEx.pointsreward || 0,
          quiztype: subEx.quiztype || "single_choice",
        }));
      
      // Berechne Gesamtpunkte für alle Teilfragen
      const totalPoints = subQuestions.reduce((sum, sq) => sum + sq.pointsreward, 0);
      
      return {
        id: ex.id,
        index: idx,
        quiztype: "container", // Neuer Typ für Container
        promptmarkdown: ex.promptmarkdown || "",
        solutionmarkdown: ex.solutionmarkdown || "",
        question: ex.question || {},
        pointsreward: totalPoints, // Summe aller Teilfragen
        timeLimitSeconds: ex.timeLimitSeconds || 60,
        isContainer: true,
        subQuestions: subQuestions,
        title: ex.title || "",
      };
    }
    
    // Normale Frage (kein Container)
    return {
      id: ex.id,
      index: idx,
      quiztype: ex.quiztype || "input",
      promptmarkdown: ex.promptmarkdown || "",
      solutionmarkdown: ex.solutionmarkdown || "",
      question: ex.question || {},
      pointsreward: ex.pointsreward || 10,
      timeLimitSeconds: ex.timeLimitSeconds || 60,
      isContainer: false,
      subQuestions: [],
    };
  });
}, [lesson]);


  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];

  const existingAnswer = useMemo(() => {
    if (!currentQuestion) return null;
    return answers.find((a) => a.questionId === currentQuestion.id) || null;
  }, [answers, currentQuestion]);

  const isLocked = !!existingAnswer;

  useEffect(() => {
    if (!currentQuestion) return;
    if (!existingAnswer) {
      setSelectedIndex(null);
      setInputValue("");
      setMultiInputValues({});
      setDropdownValues({});
      setsubQuestionsAnswers({});
      setChecked(false);
      setIsCorrect(null);
      setAiFeedbackText(null);
      setIsPaused(false);
      return;
    }

    setSelectedIndex(existingAnswer.selectedIndex ?? null);
    setInputValue(existingAnswer.inputValue ?? "");
    setMultiInputValues(existingAnswer.multiInputValues ?? {});
    setDropdownValues(existingAnswer.dropdownValues ?? {});
    setChecked(true);
    setIsCorrect(existingAnswer.isCorrect);
    setAiFeedbackText(existingAnswer.aiFeedback ?? null);
    setIsPaused(true);
  }, [currentQuestion?.id, existingAnswer]);

  useEffect(() => {
    if (!started || finished || isPaused) return;
    if (timeLeft <= 0) {
      setFinished(true);
      return;
    }

    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [started, finished, timeLeft, isPaused]);

  useEffect(() => {
    if (!started || finished) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [started, finished]);

  const progressPercent = useMemo(() => {
    if (!totalQuestions) return 0;
    return (currentIndex / totalQuestions) * 100;
  }, [currentIndex, totalQuestions]);

  const getAIFeedback = async (question, userAnswer, correctAnswer, questionId) => {
    setLoadingFeedback(true);
    setAiFeedbackText("");

    try {
      // Don't pass systemPrompt - let backend use language-aware defaults
      const response = await aiFeedback(question, userAnswer, correctAnswer);
      const feedbackContent = response?.content || t.quiz.no_explanation;
      setAiFeedbackText(feedbackContent);

      // Save feedback to answers array
      setAnswers((prev) => {
        return prev.map(a =>
          a.questionId === questionId
            ? { ...a, aiFeedback: feedbackContent }
            : a
        );
      });
    } catch (error) {
      console.error("AI Feedback Error:", error);
      const errorMsg = t.quiz.explanation_error;
      setAiFeedbackText(errorMsg);

      // Save error message to answers array too
      setAnswers((prev) => {
        return prev.map(a =>
          a.questionId === questionId
            ? { ...a, aiFeedback: errorMsg }
            : a
        );
      });
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleCheck = () => {
    if (!currentQuestion) return;
    if (isLocked) return;
    
    setIsPaused(true);

    let correct = false;
    let userAnswerText = "";
    let correctAnswerText = "";

    const quiztype = currentQuestion.quiztype;
    const questionData = currentQuestion.question;

    if (currentQuestion.isContainer && currentQuestion.subQuestions.length > 0) {
  let correctCount = 0;
  const totalSubQuestions = currentQuestion.subQuestions.length;
  const userAnswers = [];
  const correctAnswers = [];

  currentQuestion.subQuestions.forEach(subQ => {
    const userAnswer = subQuestionsAnswers[subQ.id];
    
    if (subQ.quiztype === 'single_choice') {
      const choices = subQ.question?.choices || [];
      const correctIndex = choices.findIndex(c => c.isCorrect);

      if (userAnswer === correctIndex) {
        correctCount++;
      }

      userAnswers.push(`${subQ.title}: ${choices[userAnswer]?.text || t.common.noSelection || "keine Auswahl"}`);
      correctAnswers.push(`${subQ.title}: ${choices[correctIndex]?.text || ""}`);
      
    } else if (subQ.quiztype === 'input') {
      const userInput = String(userAnswer || "").trim();
      const expectedAnswer = String(subQ.question?.answer || "").trim();
      const tolerance = subQ.question?.tolerance || 0;
      const answerFormat = subQ.question?.answerFormat || "text";
      
      let isCorrect = false;
      
      if (answerFormat === "number") {
        const userNum = parseFloat(userInput);
        const expectedNum = parseFloat(expectedAnswer);
        
        if (!isNaN(userNum) && !isNaN(expectedNum)) {
          isCorrect = Math.abs(userNum - expectedNum) <= tolerance;
        }
      } else {
        isCorrect = userInput.toLowerCase() === expectedAnswer.toLowerCase();
      }
      
      if (isCorrect) {
        correctCount++;
      }
      
      userAnswers.push(`${subQ.title}: ${userInput}`);
      correctAnswers.push(`${subQ.title}: ${expectedAnswer}${subQ.question?.unit ? ' ' + subQ.question.unit : ''}`);
    }
    else if (subQ.quiztype === 'dropdown') {
      const blanks = subQ.question?.blanks || [];
      let allBlanksCorrect = true;
      const userBlankAnswers = [];
      const correctBlankAnswers = [];
      
      blanks.forEach(blank => {
        const selectedId = userAnswer?.[blank.id];
        const selectedOption = blank.options.find(opt => opt.id === selectedId);
        const correctOption = blank.options.find(opt => opt.isCorrect);
        
        userBlankAnswers.push(selectedOption?.text || t.common.noSelection || "keine Auswahl");
        correctBlankAnswers.push(correctOption?.text || "");
        
        if (!selectedOption?.isCorrect) {
          allBlanksCorrect = false;
        }
      });
      
      if (allBlanksCorrect) {
        correctCount++;
      }
      
      userAnswers.push(`${subQ.title}: ${userBlankAnswers.join(", ")}`);
      correctAnswers.push(`${subQ.title}: ${correctBlankAnswers.join(", ")}`);
    }
  });

        // Berechne Status
        if (correctCount === totalSubQuestions) {
          correct = "all";
        } else if (correctCount > 0) {
          correct = "partial";
        } else {
          correct = false;
        }

        userAnswerText = userAnswers.join("; ");
        correctAnswerText = correctAnswers.join("; ");
    } else if (currentQuestion.quiztype === "single_choice") {
      if (selectedIndex == null) return;
      
      const choices = questionData.choices || [];
      const correctChoice = choices.find(c => c.isCorrect);
      correct = selectedIndex === choices.findIndex(c => c.isCorrect);
      userAnswerText = choices[selectedIndex]?.text || "";
      correctAnswerText = correctChoice?.text || "";
      
    } else if (currentQuestion.quiztype === "input") {
      // Check if this is a multi-part question
      if (currentQuestion.hasSubQuestions && currentQuestion.subQuestions.length > 0) {
        // Multi-part question: check all parts
        const subQs = currentQuestion.subQuestions;
        const allCorrect = true;
        const userAnswers = [];
        const correctAnswers = [];
        
        // For multi-part, we just check if they filled all fields
        // In real scenario, you'd need separate correct answers for each part
        for (const subQ of subQs) {
          const userValue = (multiInputValues[subQ.id] || "").trim();
          userAnswers.push(`${subQ.id}) ${userValue}`);
        }
        
        // Since we don't have individual answers in JSON, 
        // we'll use the final answer as reference
        const expectedAnswer = String(questionData.answer || "").trim();
        const lastSubAnswer = (multiInputValues[subQs[subQs.length - 1].id] || "").trim();
        
        const answerFormat = questionData.answerFormat || "text";
        const tolerance = questionData.tolerance || 0;
        
        if (answerFormat === "number") {
          const userNum = parseFloat(lastSubAnswer);
          const expectedNum = parseFloat(expectedAnswer);
          
          if (!isNaN(userNum) && !isNaN(expectedNum)) {
            correct = Math.abs(userNum - expectedNum) <= tolerance;
          }
        } else {
          correct = lastSubAnswer.toLowerCase() === expectedAnswer.toLowerCase();
        }
        
        userAnswerText = userAnswers.join(", ");
        correctAnswerText = expectedAnswer + (questionData.unit ? " " + questionData.unit : "");
        
      } else {
        // Single input question
        const userInput = (inputValue || "").trim();
        const expectedAnswer = String(questionData.answer || "").trim();
        const tolerance = questionData.tolerance || 0;
        const answerFormat = questionData.answerFormat || "text";
        
        if (answerFormat === "number") {
          const userNum = parseFloat(userInput);
          const expectedNum = parseFloat(expectedAnswer);
          
          if (!isNaN(userNum) && !isNaN(expectedNum)) {
            correct = Math.abs(userNum - expectedNum) <= tolerance;
          }
          
          userAnswerText = userInput;
          correctAnswerText = expectedAnswer + (questionData.unit ? " " + questionData.unit : "");
        } else {
          correct = userInput.toLowerCase() === expectedAnswer.toLowerCase();
          userAnswerText = userInput;
          correctAnswerText = expectedAnswer;
        }
      }
      
    } else if (quiztype === "dropdown") {
      const blanks = questionData.blanks || [];
      let allCorrect = true;
      
      for (const blank of blanks) {
        const userSelectedId = dropdownValues[blank.id];
        const correctOption = blank.options?.find(opt => opt.isCorrect);
        
        if (userSelectedId !== correctOption?.id) {
          allCorrect = false;
        }
      }
      
      correct = allCorrect;
      
      // Build answer texts for feedback
      const userAnswers = blanks.map(blank => {
        const selectedId = dropdownValues[blank.id];
        const option = blank.options?.find(opt => opt.id === selectedId);
        return option?.text || t.common.noSelection || "keine Auswahl";
      });
      
      const correctAnswers = blanks.map(blank => {
        const correctOpt = blank.options?.find(opt => opt.isCorrect);
        return correctOpt?.text || "";
      });
      
      userAnswerText = userAnswers.join(", ");
      correctAnswerText = correctAnswers.join(", ");
    }

    setIsCorrect(correct);
    setChecked(true);

    if (correct !== true && correct !== "all") {
      getAIFeedback(
        currentQuestion.promptmarkdown,
        userAnswerText,
        correctAnswerText,
        currentQuestion.id
      );
    }

    const qId = currentQuestion.id;
    setAnswers((prev) => {
      const other = prev.filter((a) => a.questionId !== qId);
      return [
        ...other,
        {
          questionId: qId,
          index: currentQuestion.index,
          quiztype: quiztype,
          selectedIndex,
          inputValue,
          multiInputValues: {...multiInputValues},
          dropdownValues: {...dropdownValues},
          subQuestionsAnswers: {...subQuestionsAnswers},
          isCorrect: correct,
          aiFeedback: null, // Will be updated when AI feedback arrives
        },
      ];
    });
  };

  const handleNext = () => {
    setIsPaused(false);

    if (currentIndex === totalQuestions - 1) {
      setFinished(true);
      return;
    }
    setCurrentIndex((i) => i + 1);
  };

  const handlePrev = () => {
    if (currentIndex === 0) return;
    setCurrentIndex((i) => i - 1);
  };

  const totalCorrect = answers.filter((a) => a.isCorrect === true || a.isCorrect === "all").length;
  const totalPartial = answers.filter((a) => a.isCorrect === "partial").length;
  const totalWrong = totalQuestions - totalCorrect - totalPartial;
  const pointsPerQuestion = currentQuestion?.pointsreward || 10;
  const totalScore = answers.reduce((sum, a) => {
    const q = questions.find(q => q.id === a.questionId);

    if (q?.isContainer && a.isCorrect === "partial") {
      const correctCount = q.subQuestions.filter((subQ) => {
        const userChoice = a.subQuestionsAnswers?.[subQ.id];
        const correctIdx = subQ.question?.choices?.findIndex(c => c.isCorrect);
        return userChoice === correctIdx;
      }).length;

      const pointsPerSub = q.pointsreward / q.subQuestions.length;
      return sum + (correctCount * pointsPerSub);
    }

    return sum + ((a.isCorrect === "all" || a.isCorrect === true) ? (q?.pointsreward || 10) : 0);
  }, 0);

  const percentage =
    totalQuestions > 0
      ? Math.round((totalCorrect / totalQuestions) * 100)
      : 0;

  useEffect(() => {
    if (!finished) return;
    if (!user || !lesson) return;
    if (savedResult) return;

    if (
      myResults.some(
        (r) => r.quiz_id === lesson.id && r.user_id === user.id
      )
    ) {
      setSavedResult(true);
      return;
    }

    (async () => {
      try {
        const payload = {
          quiz_id: lesson.id,
          user_id: user.id,
          score: totalScore,
          total_questions: totalQuestions,
          correct_answers: totalCorrect,
          time_taken_seconds: 0,
          percentage,
          answers: answers.map((a) => ({
            question_index: a.index,
            selected_answer:
              typeof a.selectedIndex === "number" ? a.selectedIndex : null,
            is_correct: !!a.isCorrect,
            time_taken: 0,
          })),
        };

        await createQuizResult(payload);

        const now = new Date();
        const completedDate = now.toISOString().slice(0, 19).replace("T", " ");

        await upsertLessonProgress({
          id: undefined,
          lesson_id: lesson.id,
          user_id: user.id,
          status: "completed",
          progress_percent: 100,
          time_spent_minutes: 0,
          completed_date: completedDate,
        });

        setSavedResult(true);
      } catch (e) {
        console.error("Fehler beim Speichern des Quiz-Ergebnisses:", e);
      }
    })();
  }, [
    finished,
    user,
    lesson,
    totalScore,
    totalQuestions,
    totalCorrect,
    percentage,
    answers,
    myResults,
    savedResult,
  ]);

  // Helper function to check if answer is ready
  const isAnswerReady = () => {
    if (!currentQuestion) return false;
    if (currentQuestion.isContainer && currentQuestion.subQuestions.length > 0) {
      return currentQuestion.subQuestions.every(subQ => {
        const answer = subQuestionsAnswers[subQ.id];

        if(subQ.quiztype === 'single_choice') {
          return answer !== undefined && answer !== null;
        }

        if (subQ.quiztype === 'input') {
          return typeof answer === 'string' && answer.trim().length > 0;
        }

        if (subQ.quiztype === 'dropdown') {
          const blanks = subQ.question?.blanks || [];
          return blanks.every(blank => {
            const selected = answer?.[blank.id];
            return selected && selected !== "";
          });
        }
        return true;
      });
    }
    const quiztype = currentQuestion.quiztype;
    
    if (quiztype === "single_choice") {
      return selectedIndex != null;
    } else if (quiztype === "input") {
      if (currentQuestion.hasSubQuestions) {
        // Check if all sub-questions are answered
        return currentQuestion.subQuestions.every(sq => 
          (multiInputValues[sq.id] || "").trim().length > 0
        );
      }
      return inputValue.trim().length > 0;
    } else if (quiztype === "dropdown") {
      const blanks = currentQuestion.question?.blanks || [];
      return blanks.every(blank => dropdownValues[blank.id] != null);
    }
    
    return false;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">{t.quiz.loading}</p>
      </Container>
    );
  }

  if (error || !lesson || !moduleData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || t.quiz.error_loading}
        </Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          {t.common.back}
        </Button>
      </Container>
    );
  }

  if (finished) {
    return (
      <Container className="py-5" style={{ maxWidth: 600 }}>
        <Card className="shadow">
          <Card.Body className="text-center p-5">
            <Trophy size={64} className="text-warning mb-3" />
            <h2 className="mb-4">{t.quiz.completed}</h2>
            <p className="lead">
              {totalCorrect} {t.quiz.of} {totalQuestions} {t.quiz.questions_correct}
            </p>
            <h1 className="display-4 text-primary">{percentage}%</h1>

            <div className="d-flex justify-content-around my-4">
              <div>
                <h3 className="text-success">{totalScore}</h3>
                <small className="text-muted">{t.quiz.points}</small>
              </div>
              <div>
                <h3 className="text-primary">{totalCorrect}</h3>
                <small className="text-muted">{t.quiz.correct}</small>
              </div>
              <div>
                <h3 className="text-primary">{totalPartial}</h3>
                <small className="text-muted">{t.quiz.partial}</small>
              </div>
              <div>
                <h3 className="text-danger">{totalQuestions - totalCorrect}</h3>
                <small className="text-muted">{t.quiz.incorrect}</small>
              </div>
            </div>

            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(-1)}
              className="mt-3"
            >
              {t.quiz.back_to_overview}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!started && alreadyCompleted) {
    return (
      <Container className="py-5" style={{ maxWidth: 600 }}>
        <Alert variant="warning">
          <h4>{t.quiz.already_completed_title}</h4>
          <p>{t.quiz.already_completed_text}</p>
        </Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          {t.common.back}
        </Button>
      </Container>
    );
  }

  if (!started) {
    const displayTimeMinutes = Math.floor(timeLeft / 60);
    return (
      <Container className="py-5" style={{ maxWidth: 600 }}>
        <Card className="shadow">
          <Card.Body className="p-4">
            <h2 className="mb-3">{lesson.title}</h2>
            <p className="text-muted">
              {t.quiz.start_description.replace('{{module}}', moduleData.title)}
            </p>

            <div className="my-4 p-3 bg-light rounded">
              <div className="d-flex justify-content-between mb-2">
                <span>
                  <strong>{t.quiz.total_questions}</strong>
                </span>
                <span>{totalQuestions}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>
                  <strong>{t.quiz.time_limit}</strong>
                </span>
                <span>{displayTimeMinutes} min</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>
                  <strong>{t.quiz.average_points}</strong>
                </span>
                <span>{pointsPerQuestion}</span>
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
              >
                {t.common.back}
              </Button>
              <Button
                variant="primary"
                className="flex-grow-1"
                onClick={() => setStarted(true)}
              >
                {t.quiz.start_button}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Render question based on type
  const renderQuestion = () => {
    if (!currentQuestion) return null;
    
    console.log('=== renderQuestion DEBUG ===');
    console.log('currentQuestion:', currentQuestion);
    console.log('isContainer:', currentQuestion.isContainer);
    console.log('subQuestions length:', currentQuestion.subQuestions?.length);
    console.log('quiztype:', currentQuestion.quiztype);

    const quiztype = currentQuestion.quiztype;
    const questionData = currentQuestion.question;

   if (currentQuestion.isContainer && currentQuestion.subQuestions.length > 0) {
  
    return (
      <>
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({node, ...props}) => <p style={{marginBottom: '1rem'}} {...props} />,
            strong: ({node, ...props}) => <strong {...props} />,
            em: ({node, ...props}) => <em {...props} />,
          }}
        >
          {currentQuestion.promptmarkdown}
        </ReactMarkdown>

        {currentQuestion.subQuestions.map((subQ, subIdx) => {
  return (
    <div 
      key={subQ.id} 
      style={{
        marginTop: '1.5rem', 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
      }}
    >
      <h6 style={{fontWeight: 'bold', marginBottom: '0.75rem', color: '#495057'}}>
        {subQ.title || `${t.common.part} ${String.fromCharCode(97 + subIdx)})`}
      </h6>
      
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
          strong: ({node, ...props}) => <strong {...props} />,
        }}
      >
        {subQ.promptmarkdown}
      </ReactMarkdown>

      {subQ.quiztype === 'single_choice' && (
      <div className="d-grid gap-2 mt-3">
        {(subQ.question?.choices || []).map((choice, choiceIdx) => {
          const isSelectedChoice = subQuestionsAnswers[subQ.id] === choiceIdx;
          let variant = "outline-secondary";
          let icon = null; // NEU
          
          if (checked) {
            if (choice.isCorrect) {
              // Diese Antwort ist richtig
              variant = "success";
              icon = <CheckCircle2 size={18} className="me-2" />; // NEU
            } else if (isSelectedChoice && !choice.isCorrect) {
              // User hat falsche Antwort gewählt
              variant = "danger";
              icon = <XCircle size={18} className="me-2" />; // NEU
            }
          } else if (isSelectedChoice) {
            variant = "primary";
          }

          return (
            <Button
              key={choice.id}
              variant={variant}
              disabled={isLocked || checked}
              onClick={() => {
                if (!isLocked && !checked) {
                  setsubQuestionsAnswers(prev => ({  // ACHTUNG: Großes S in setSubQuestionsAnswers!
                    ...prev,
                    [subQ.id]: choiceIdx
                  }));
                }
              }}
              className="text-start p-3"
            >
              {icon}
              {choice.text}
            </Button>
          );
        })}
      </div>
      )}

    {subQ.quiztype === 'input' && (
      <div className="mt-3"> 
        <Form.Control type="text" placeholder={t.quiz.enter_answer || "Antwort eingeben"} value={subQuestionsAnswers[subQ.id] || ""} onChange={(e) => {
          if (!isLocked && !checked) {
            setsubQuestionsAnswers(prev => ({
              ...prev,
              [subQ.id]: e.target.value
            }));
          }
        }}
        disabled={isLocked || checked}
        />
        {subQ.question?.unit && (<Form.Text className="text-muted">{t.common.unit}: {subQ.question.unit}</Form.Text>)}
      </div>
    )}

    {subQ.quiztype === 'dropdown' && (
              <div className="mt-3">
                {subQ.question?.textSegments && subQ.question?.blanks && (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {subQ.question.textSegments.map((segment, segIdx) => {
                      const blank = subQ.question.blanks[segIdx];
                      
                      return (
                        <React.Fragment key={segIdx}>
                          {segment && <span>{segment}</span>}
                          
                          {blank && (
                            <Form.Select
                              style={{ width: 'auto', display: 'inline-block', minWidth: '200px' }}
                              value={subQuestionsAnswers[subQ.id]?.[blank.id] || ""}
                              onChange={(e) => {
                                if (!isLocked && !checked) {
                                  setsubQuestionsAnswers(prev => ({
                                    ...prev,
                                    [subQ.id]: {
                                      ...(prev[subQ.id] || {}),
                                      [blank.id]: e.target.value
                                    }
                                  }));
                                }
                              }}
                              disabled={isLocked || checked}
                              className={
                                checked
                                  ? blank.options.find(opt => opt.id === subQuestionsAnswers[subQ.id]?.[blank.id])?.isCorrect
                                    ? "border-success"
                                    : "border-danger"
                                  : ""
                              }
                            >
                              <option value="">{t.quiz.choose || "-- Wählen --"}</option>
                              {blank.options.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.text}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
                </div>
            )}
    </div>
  );
})}

      </>
    ); 
}


    if (quiztype === "single_choice") {
      const choices = questionData.choices || [];
      
      return (
        <div className="d-flex flex-column gap-2">
          {choices.map((choice, idx) => {
            const isSelected = selectedIndex === idx;
            let variant = "outline-secondary";
            
            if (checked) {
              if (choice.isCorrect) {
                variant = "success";
              } else if (isSelected && !choice.isCorrect) {
                variant = "danger";
              }
            } else if (isSelected) {
              variant = "primary";
            }
            
            return (
              <Button
                key={choice.id}
                variant={variant}
                className="text-start p-3"
                onClick={() => {
                  if (isLocked) return;
                  setSelectedIndex(idx);
                  setChecked(false);
                  setIsCorrect(null);
                  setAiFeedbackText(null);
                }}
                disabled={isLocked}
              >
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({node, ...props}) => <span style={{margin: '0'}} {...props} />,
                    code: ({node, inline, ...props}) => (
                      inline 
                        ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                        : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                    ),
                    strong: ({node, ...props}) => <strong {...props} />,
                    em: ({node, ...props}) => <em {...props} />,
                    h1: ({node, ...props}) => <h5 style={{fontSize: '0.9em', margin: '0.2em 0', fontWeight: 'bold'}} {...props} />,
                    h2: ({node, ...props}) => <h5 style={{fontSize: '0.9em', margin: '0.2em 0', fontWeight: 'bold'}} {...props} />,
                    h3: ({node, ...props}) => <h5 style={{fontSize: '0.9em', margin: '0.2em 0', fontWeight: 'bold'}} {...props} />,
                    ul: ({node, ...props}) => <ul style={{margin: '0.2em 0', paddingLeft: '1em'}} {...props} />,
                    ol: ({node, ...props}) => <ol style={{margin: '0.2em 0', paddingLeft: '1em'}} {...props} />,
                    li: ({node, ...props}) => <li style={{margin: '0.1em 0'}} {...props} />,
                  }}
                >
                  {choice.text}
                </ReactMarkdown>
              </Button>
            );
          })}
        </div>
      );
      
    } else if (quiztype === "input") {
      const unit = questionData.unit || "";
      
      // Check if this has sub-questions (auto-detected)
      if (currentQuestion.hasSubQuestions && currentQuestion.subQuestions.length > 0) {
        return (
          <div className="d-flex flex-column gap-3">
            {currentQuestion.subQuestions.map(subQ => (
              <div key={subQ.id}>
                <Form.Label>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({node, ...props}) => <span style={{margin: '0'}} {...props} />,
                      code: ({node, inline, ...props}) => (
                        inline 
                          ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                          : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                      ),
                      strong: ({node, ...props}) => <strong {...props} />,
                      em: ({node, ...props}) => <em {...props} />,
                    }}
                  >
                    {subQ.label}
                  </ReactMarkdown>
                  {unit && ` (in ${unit})`}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t.quiz.enter_answer}
                  value={multiInputValues[subQ.id] || ""}
                  onChange={(e) => {
                    if (isLocked) return;
                    setMultiInputValues(prev => ({
                      ...prev,
                      [subQ.id]: e.target.value
                    }));
                    setChecked(false);
                    setIsCorrect(null);
                    setAiFeedbackText(null);
                  }}
                  disabled={isLocked}
                />
              </div>
            ))}
          </div>
        );
      }
      
      // Single input
      return (
        <>
          <Form.Label>{t.quiz.your_answer} {unit && `(in ${unit})`}</Form.Label>
          <Form.Control
            type="text"
            placeholder={t.quiz.enter_answer}
            value={inputValue}
            onChange={(e) => {
              if (isLocked) return;
              setInputValue(e.target.value);
              setChecked(false);
              setIsCorrect(null);
              setAiFeedbackText(null);
            }}
            disabled={isLocked}
          />
        </>
      );
      
    } else if (quiztype === "dropdown") {
      const textSegments = questionData.textSegments || [];
      const blanks = questionData.blanks || [];
      
      return (
        <div className="mb-3">
          {questionData.prompt && (
            <div className="mb-3">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                  h1: ({node, ...props}) => <h1 style={{fontSize: '1.1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  h2: ({node, ...props}) => <h2 style={{fontSize: '1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  h3: ({node, ...props}) => <h3 style={{fontSize: '0.95em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  code: ({node, inline, ...props}) => (
                    inline 
                      ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                      : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                  ),
                  pre: ({node, ...props}) => <pre style={{margin: '0.5em 0', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                  ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                  li: ({node, ...props}) => <li style={{margin: '0.25em 0'}} {...props} />,
                  strong: ({node, ...props}) => <strong {...props} />,
                  em: ({node, ...props}) => <em {...props} />,
                }}
              >
                {questionData.prompt}
              </ReactMarkdown>
            </div>
          )}
          
          <div>
  {textSegments.map((segment, idx) => {
    const blank = blanks[idx];
    const cleanSegment = segment.replace(/\s*—\s*$/, '');
    
    return (
      <div key={idx} style={{ marginBottom: '1rem' }}>
        {cleanSegment && (
          <div style={{ marginBottom: '0.5rem' }}>
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({node, ...props}) => <span style={{margin: '0'}} {...props} />,
                code: ({node, inline, ...props}) => (
                  inline 
                    ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                    : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                ),
                strong: ({node, ...props}) => <strong {...props} />,
                em: ({node, ...props}) => <em {...props} />,
              }}
            >
              {cleanSegment}
            </ReactMarkdown>
          </div>
        )}
        
        {blank && (
          <Form.Select
            style={{ 
              width: "100%", 
              maxWidth: "350px"
            }}
            value={dropdownValues[blank.id] || ""}
            onChange={(e) => {
              if (isLocked) return;
              setDropdownValues(prev => ({
                ...prev,
                [blank.id]: e.target.value
              }));
              setChecked(false);
              setIsCorrect(null);
              setAiFeedbackText(null);
            }}
            disabled={isLocked}
          >
            <option value="">{t.quiz.choose}</option>
            {blank.options?.map(option => (
              <option key={option.id} value={option.id}>
                {option.text}
              </option>
            ))}
          </Form.Select>
        )}
      </div>
    );
  })}
</div>

        </div>
      );
    }
    
    return null;
  };

  return (
    <Container className="py-4" style={{ maxWidth: 800 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="m-0">Quiz</h5>
        <div className="d-flex align-items-center gap-2">
          <Clock size={18} />
          <span>
            {Math.floor(timeLeft / 60)}:
            {String(timeLeft % 60).padStart(2, "0")}
          </span>
        </div>
      </div>

      <ProgressBar
        now={progressPercent}
        className="mb-4"
        style={{ height: 8 }}
      />

      <Card className="shadow-sm">
        <Card.Body className="p-4">
          <div className="mb-3">
            <small className="text-muted">
              {t.quiz.question} {currentIndex + 1} {t.quiz.of} {totalQuestions}
            </small>
          </div>

          <div className="mb-4">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                h1: ({node, ...props}) => <h1 style={{fontSize: '1.5em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                h2: ({node, ...props}) => <h2 style={{fontSize: '1.3em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                h3: ({node, ...props}) => <h3 style={{fontSize: '1.1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                code: ({node, inline, ...props}) => (
                  inline 
                    ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                    : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                ),
                pre: ({node, ...props}) => <pre style={{margin: '0.5em 0', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto'}} {...props} />,
                ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                li: ({node, ...props}) => <li style={{margin: '0.25em 0'}} {...props} />,
                blockquote: ({node, ...props}) => <blockquote style={{margin: '0.5em 0', paddingLeft: '1em', borderLeft: '3px solid #dee2e6', color: '#6c757d'}} {...props} />,
                a: ({node, ...props}) => <a style={{color: '#0d6efd'}} {...props} target="_blank" rel="noopener noreferrer" />,
                table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', margin: '0.5em 0', width: '100%'}} {...props} />,
                th: ({node, ...props}) => <th style={{border: '1px solid #dee2e6', padding: '0.5em', backgroundColor: '#f8f9fa'}} {...props} />,
                td: ({node, ...props}) => <td style={{border: '1px solid #dee2e6', padding: '0.5em'}} {...props} />,
              }}
            >
              {currentQuestion?.promptmarkdown || t.quiz.question}
            </ReactMarkdown>
          </div>

          {renderQuestion()}

          {checked && (
  <Alert
    variant={
      isCorrect === "all" || isCorrect === true ? "success" : 
      isCorrect === "partial" ? "warning" : 
      "danger"
    }
    className="mt-3 d-flex align-items-start"
  >
    {(isCorrect === "all" || isCorrect === true) ? (
      <CheckCircle2 className="me-2 flex-shrink-0" size={20} />
    ) : isCorrect === "partial" ? (
      <CheckCircle2 className="me-2 flex-shrink-0" size={20} />
    ) : (
      <XCircle className="me-2 flex-shrink-0" size={20} />
    )}
    <div className="flex-grow-1">
      <strong>
        {(isCorrect === "all" || isCorrect === true) 
          ? t.quiz.correct 
          : isCorrect === "partial"
          ? t.quiz.partial
          : t.quiz.incorrect
        }
      </strong>

      {/* Zeige KI-Feedback und Lösung nur bei falschen/teilweise richtigen Antworten */}
      {(isCorrect === false || isCorrect === "partial") && (
        <>
          {loadingFeedback && (
            <div className="mt-2 p-2 bg-light rounded">
              <Spinner animation="border" size="sm" className="me-2" />
              <small className="text-muted">{t.quiz.ai_loading}</small>
            </div>
          )}

          {aiFeedbackText && (
            <div className="mt-2 p-3 bg-light rounded border-start border-primary border-3">
              <div className="d-flex align-items-center mb-2">
                <Cpu size={16} className="me-2 text-primary" />
                <strong className="text-primary">{t.quiz.ai_explanation}</strong>
              </div>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                  code: ({node, inline, ...props}) => (
                    inline 
                      ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                      : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                  ),
                  pre: ({node, ...props}) => <pre style={{margin: '0.5em 0', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                  ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                  li: ({node, ...props}) => <li style={{margin: '0.25em 0'}} {...props} />,
                  blockquote: ({node, ...props}) => <blockquote style={{margin: '0.5em 0', paddingLeft: '1em', borderLeft: '3px solid #dee2e6', color: '#6c757d'}} {...props} />,
                  h1: ({node, ...props}) => <h1 style={{fontSize: '1.5em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  h2: ({node, ...props}) => <h2 style={{fontSize: '1.3em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  h3: ({node, ...props}) => <h3 style={{fontSize: '1.1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  a: ({node, ...props}) => <a style={{color: '#0d6efd'}} {...props} target="_blank" rel="noopener noreferrer" />,
                  table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', margin: '0.5em 0', width: '100%'}} {...props} />,
                  th: ({node, ...props}) => <th style={{border: '1px solid #dee2e6', padding: '0.5em', backgroundColor: '#f8f9fa'}} {...props} />,
                  td: ({node, ...props}) => <td style={{border: '1px solid #dee2e6', padding: '0.5em'}} {...props} />,
                }}
              >
                {aiFeedbackText}
              </ReactMarkdown>
            </div>
          )}

          {currentQuestion.solutionmarkdown && (
            <div className="mt-2 p-3 bg-white rounded border border-success border-start border-success border-3">
              <div className="d-flex align-items-center mb-2">
                <strong className="text-success">{t.quiz.solution}</strong>
              </div>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                  code: ({node, inline, ...props}) => (
                    inline 
                      ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                      : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                  ),
                  pre: ({node, ...props}) => <pre style={{margin: '0.5em 0', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                  ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                  li: ({node, ...props}) => <li style={{margin: '0.25em 0'}} {...props} />,
                  strong: ({node, ...props}) => <strong {...props} />,
                  em: ({node, ...props}) => <em {...props} />,
                  h1: ({node, ...props}) => <h4 style={{fontSize: '1.1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  h2: ({node, ...props}) => <h4 style={{fontSize: '1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  h3: ({node, ...props}) => <h5 style={{fontSize: '0.95em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                  blockquote: ({node, ...props}) => <blockquote style={{margin: '0.5em 0', paddingLeft: '1em', borderLeft: '3px solid #dee2e6', color: '#6c757d'}} {...props} />,
                }}
              >
                {currentQuestion.solutionmarkdown}
              </ReactMarkdown>
            </div>
          )}
        </>
      )}
    </div>
  </Alert>
)}

          <div className="d-flex gap-2 mt-4">
            <Button
              variant="outline-secondary"
              onClick={handlePrev}
              disabled={currentIndex === 0}
            >
              <ArrowLeft size={18} className="me-1" />
              {t.common.back}
            </Button>

            {!checked && (
              <Button
                variant="primary"
                className="flex-grow-1"
                onClick={handleCheck}
                disabled={!isAnswerReady()}
              >
                {t.quiz.check_answer}
              </Button>
            )}

            {checked && (
              <Button
                variant="primary"
                className="flex-grow-1"
                onClick={handleNext}
              >
                {currentIndex === totalQuestions - 1
                  ? t.quiz.finish_quiz
                  : t.quiz.next_question}
                <ArrowRight size={18} className="ms-1" />
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}